import { Alert, App as AntApp, Button, Card, Form, Input, InputNumber, Popconfirm, Select, Space, Table, Tag, Typography } from "antd";
import { useCallback, useState } from "react";
import { api, errorText } from "../api";
import { dateTime } from "../format";
import { canManage, useSession } from "../session";
import { useResource } from "../useResource";

/** `PersonaView` */
export interface PersonaVersion {
  version: number;
  assistantName: string;
  tone: string;
  greeting: string;
  bannedPhrases: string[];
  maxDiscountPercent: number;
  createdBy: number;
  createdAt: string;
}

interface Overview {
  publishedVersion: number;
  versions: PersonaVersion[];
}

type Draft = Omit<PersonaVersion, "version" | "createdBy" | "createdAt">;

const EMPTY: Draft = { assistantName: "", tone: "", greeting: "", bannedPhrases: [], maxDiscountPercent: 0 };

/**
 * Persona sürümleri. Bir sürüm oluşturulduktan sonra değişmez; düzenleme yeni sürüm üretir. Geri alma, eski bir
 * sürümü yeniden yayınlamaktır. İndirim üst sınırı agent tarafından kodla uygulanır (ADR 008).
 */
export function PersonaPage() {
  const me = useSession((s) => s.me);
  const manage = canManage(me);
  const { message } = AntApp.useApp();
  const [form] = Form.useForm<Draft>();
  const [saving, setSaving] = useState(false);
  const { data: overview, error, reload } = useResource(useCallback(async () => {
    const o = await api.get<Overview>("/api/console/persona");
    // Yeni sürüm formu yayındaki (yoksa en son) sürümden başlar.
    const base = o.versions.find((v) => v.version === o.publishedVersion) ?? o.versions[0];
    form.setFieldsValue(base ? pick(base) : EMPTY);
    return o;
  }, [form]));

  async function create(draft: Draft) {
    setSaving(true);
    try {
      const { version } = await api.post<{ version: number }>("/api/console/persona/versions", draft);
      message.success(`Sürüm ${version} oluşturuldu; yayınlamak için listeden seçin`);
      reload();
    } catch (e) {
      message.error(errorText(e));
    } finally {
      setSaving(false);
    }
  }

  async function publish(version: number) {
    try {
      await api.post(`/api/console/persona/versions/${version}/publish`);
      message.success(`Sürüm ${version} yayında`);
      reload();
    } catch (e) {
      message.error(errorText(e));
    }
  }

  if (error) {
    return <Alert type="error" title={error} showIcon />;
  }
  if (!overview) {
    return null;
  }
  return (
    <Space orientation="vertical" size="large" style={{ width: "100%" }}>
      <Typography.Title level={3}>Persona</Typography.Title>
      {overview.publishedVersion === 0 && (
        <Alert type="info" showIcon title="Henüz yayında bir persona yok; asistan varsayılan kimlikle konuşur." />
      )}
      <Card title="Sürümler">
        <Table<PersonaVersion> rowKey="version" dataSource={overview.versions} pagination={false} size="small"
          columns={[
            { title: "Sürüm", dataIndex: "version", width: 90,
              render: (v: number) => (v === overview.publishedVersion ? <Space>{v}<Tag color="green">Yayında</Tag></Space> : v) },
            { title: "Asistan adı", dataIndex: "assistantName" },
            { title: "İndirim sınırı", dataIndex: "maxDiscountPercent", render: (v: number) => `%${v}` },
            { title: "Oluşturulma", dataIndex: "createdAt", render: dateTime },
            ...(manage ? [{
              title: "", key: "actions", width: 140,
              render: (_: unknown, row: PersonaVersion) => row.version === overview.publishedVersion ? null : (
                <Popconfirm title={`Sürüm ${row.version} yayınlansın mı?`}
                  description={row.version < overview.publishedVersion ? "Önceki bir sürüme geri dönülür." : undefined}
                  okText="Yayınla" cancelText="Vazgeç" onConfirm={() => publish(row.version)}>
                  <Button size="small">{row.version < overview.publishedVersion ? "Geri al" : "Yayınla"}</Button>
                </Popconfirm>
              ),
            }] : []),
          ]} />
      </Card>
      {manage && (
        <Card title="Yeni sürüm" extra={<Typography.Text type="secondary">Yayındaki sürümden başlar</Typography.Text>}>
          <Form form={form} layout="vertical" onFinish={create} initialValues={EMPTY}>
            <Form.Item label="Asistan adı" name="assistantName" rules={[{ required: true, max: 80 }]}>
              <Input />
            </Form.Item>
            <Form.Item label="Konuşma tonu" name="tone" rules={[{ required: true, max: 500 }]}>
              <Input.TextArea autoSize={{ minRows: 2 }} />
            </Form.Item>
            <Form.Item label="Karşılama mesajı" name="greeting" rules={[{ required: true, max: 500 }]}>
              <Input.TextArea autoSize={{ minRows: 2 }} />
            </Form.Item>
            <Form.Item label="Yasaklı ifadeler" name="bannedPhrases"
              tooltip="Asistanın hiçbir koşulda kullanmayacağı ifadeler">
              <Select mode="tags" open={false} tokenSeparators={[","]} />
            </Form.Item>
            <Form.Item label="En fazla indirim (%)" name="maxDiscountPercent" rules={[{ required: true }]}
              tooltip="Asistan bu oranın üzerinde indirim öneremez">
              <InputNumber min={0} max={50} step={0.5} />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={saving}>Sürüm oluştur</Button>
          </Form>
        </Card>
      )}
    </Space>
  );
}

function pick(v: PersonaVersion): Draft {
  return {
    assistantName: v.assistantName,
    tone: v.tone,
    greeting: v.greeting,
    bannedPhrases: v.bannedPhrases,
    maxDiscountPercent: Number(v.maxDiscountPercent),
  };
}
